import {
  Button,
  CardHeader,
  CardTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiPalette } from 'react-icons/pi';
import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import { LegendColor, useMapLegendColors } from '@ais-ui/websockets';

import { cn } from '@ais-ui/utils';
import { noop } from 'lodash';

import { useMapLegendSettings } from './ColorBy/useMapLegendSettings';
import { useLayers } from '@ais-ui/react-openlayers';
import { ColourByOptions } from './ColorBy/ColourByOptions';
import { ColorByShipType } from './ColorBy/ColorByShipType';
import { ColorBySourceType } from './ColorBy/ColorBySourceType';
import { ColorByTransponderType } from './ColorBy/ColorByTransponderType';
import { ColorByShipList } from './ColorBy/ColorByShipList';
import { ColorByTargetAge, UpdateTargetAge } from './ColorBy/ColorByTargetAge';
import { useLocalization } from '@ais-ui/locale-management';

interface LegendItemProps {
  color?: string | null;
  icon?: React.ElementType | null;
  category: string;
  removable?: boolean;
  onRemove?: (event: React.MouseEvent, category: string) => void;
  id?: string;
  iconColor?: string | null;
}

export function LegendItem({
  color,
  icon: Icon,
  category,
  removable = false,
  onRemove,
  id,
  iconColor,
}: LegendItemProps) {
  const { layers, toggleLayerVisibility } = useLayers();
  const { settings } = useSettings();

  const notShownLegendItems = useMemo(() => {
    return settings['ribbonPlugin.settings']?.['ship_color_noshow_sel'] || [];
  }, [settings]);

  const [isActive, setIsActive] = useState<boolean>(
    !notShownLegendItems.includes(category)
  );

  useEffect(() => {
    setIsActive(!notShownLegendItems.includes(category));
  }, [category, notShownLegendItems]);

  const { onActivate } = useMapLegendSettings();

  const extendedOnActivate = (category: string) => {
    if (!isActive) {
      const layer = layers.find((l) => l.name.toLowerCase() === 'ships');

      if (layer) {
        toggleLayerVisibility(layer.layer, true);
      }
    }

    setIsActive(!isActive);
    onActivate(isActive, category, id);
  };

  return (
    <div
      onClick={() => extendedOnActivate(category)}
      className={cn(
        'w-32 min-w-fit min-h-fit bg-card flex items-center relative rounded-md shadow-md hover:bg-secondary hover:cursor-pointer py-1',
        !isActive && 'opacity-50'
      )}
    >
      <div>
        {isActive && color && (
          <div
            className="absolute -top-1 -left-1 h-3 w-3 z-10 shadow-lg rounded-lg"
            style={{ backgroundColor: color }}
          ></div>
        )}

        {isActive && Icon && (
          <div className="absolute -top-1 -left-1 h-3 w-3 z-10 shadow-lg rounded-lg">
            <Icon style={{ color: iconColor }} />
          </div>
        )}
        <div className="ml-4 mr-1 min-w-fit min-h-fit z-0 whitespace-normal text-xs">
          {category}
        </div>
      </div>
      {removable && onRemove && (
        <div
          onClick={(event) => onRemove(event, category)}
          className="absolute mb-0.5 right-1.5 text-muted-foreground hover:text-foreground cursor-pointer"
        >
          &times;
        </div>
      )}
    </div>
  );
}

interface ShipsLegendProps {
  className?: string;
}

export const ColorTargetsBy = React.memo(({ className }: ShipsLegendProps) => {
  const [open, setOpen] = useState(false);
  const { messages } = useLocalization();
  const { settings } = useSettings();

  const [numIntervals, setNumIntervals] = useState(
    settings['ribbonPlugin.settings']['legend_age_intervals'] ?? 2
  );
  const [intervalSize, setIntervalSize] = useState(
    settings['ribbonPlugin.settings']['legend_age_intervalsize'] ?? 15
  );

  const { onDisableAll, onEnableAll } = useMapLegendSettings();
  const { layers, toggleLayerVisibility } = useLayers();

  const shipLayer = useMemo(
    () => layers.find((l) => l.name.toLowerCase() === 'ships'),
    [layers]
  );

  const hideShowAllText = useMemo(
    () =>
      !shipLayer ? 'Show All' : shipLayer.isVisible ? 'Hide All' : 'Show All',
    [shipLayer]
  );

  const colorStateChecks = useMemo(
    () => ({
      isShiplist:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendShipList',
      isTransponderType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendTransponderType',
      isShipType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendShipType',
      isSourceType:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendSourceType',
      isTargetAge:
        settings?.['ribbonPlugin.settings']?.['ship_color_state'] ===
        'LegendAge',
    }),
    [settings]
  );

  const toggleVisibility = React.useCallback(() => {
    if (shipLayer) {
      if (shipLayer.isVisible) {
        onDisableAll();
        toggleLayerVisibility(shipLayer.layer, false);
      } else {
        onEnableAll();
        toggleLayerVisibility(shipLayer.layer, true);
      }
    }
  }, [shipLayer, onDisableAll, onEnableAll, toggleLayerVisibility]);

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className={cn(
                  'flex items-center justify-center h-8 w-8 p-1 group/btn rounded-lg',
                  className
                )}
                onClick={() => setOpen((prev) => !prev)}
              >
                <PiPalette className="h-8 w-8 group-hover/btn:text-primary" />
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>{messages.mapColorByButtonTooltip}</TooltipContent>
          <PopoverContent
            side="right"
            align="end"
            className="bg-background w-fit"
          >
            <CardHeader className="p-2 space-y-6">
              <div className="flex justify-between">
                <CardTitle className="text-lg font-medium">Color By</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mt-5 -mr-5"
                  onClick={() => setOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </Button>
              </div>
            </CardHeader>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between space-x-36">
                <ColourByOptions />
                <div className="flex">
                  {colorStateChecks.isTargetAge && (
                    <UpdateTargetAge
                      intervalSize={intervalSize}
                      numIntervals={numIntervals}
                    />
                  )}
                  <Button variant="ghost" onClick={toggleVisibility}>
                    {hideShowAllText}
                  </Button>
                </div>
              </div>
              {colorStateChecks.isShipType && <ColorByShipType />}
              {colorStateChecks.isSourceType && <ColorBySourceType />}
              {colorStateChecks.isTransponderType && <ColorByTransponderType />}
              {colorStateChecks.isShiplist && <ColorByShipList />}
              {colorStateChecks.isTargetAge && (
                <ColorByTargetAge
                  numIntervals={numIntervals}
                  intervalSize={intervalSize}
                  setIntervalSize={setIntervalSize}
                  setNumIntervals={setNumIntervals}
                />
              )}
            </div>
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
});
